/**
 * DashboardTaskList - "My Tasks" dashboard section.
 *
 * Lists every Work Order and Inspection in the signed-in user's scope
 * scheduled for today and yesterday, in one combined table. Scope is
 * enforced server-side by the /scheduled-events feed (the same one the
 * Calendar and Map consume):
 *	 - super_admin / developer            : all events platform-wide
 *	 - admin / manager / company_admin    : events for the user's companies
 *	 - technician / reviewer              : only events the user is on
 *
 * The section renders the shared JobListMain organism (the same list
 * body the /work-orders and /inspections pages use via JobListTemplate)
 * so the dashboard rows look and behave like the full list pages,
 * including the status progress bar and the Start / Resume / View CTA.
 *
 * A simplified filter row sits above the table:
 *	 - Completion chips: All / Not Completed / Completed
 *	 - User select      (options derived from the fetched events)
 *	 - Company select   (options derived from the fetched events)
 * The selects only render when there's more than one option, so a
 * technician (whose feed only ever contains themselves) isn't shown a
 * one-entry dropdown. Filtering and pagination are client-side — the
 * two-day window keeps the payload small.
 *
 * "View all" links jump to the full list pages: /inspections and
 * /work-orders (the rows are individual work orders, so the WO list is
 * the right destination for every role). Rows belonging to a parent
 * Project Work Order show the association under the job title — linked
 * to the project detail for the admin tier that can open that page.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../hooks/useAuth';
import {
	scheduledEventsService,
	getEventCategory,
} from '../../../services/scheduledEventsService';
import type { ScheduledEvent } from '../../../services/scheduledEventsService';
import { workOrderService } from '../../../services/workOrderService';
import { inspectionService } from '../../../services/inspectionService';
import { qk } from '../../../lib/queryKeys';
import { isoDaysAgo } from '../../../utils/formatDate';
import { getJobProgress } from '../../../utils/jobProgress';
import { EventTypeBadge } from '../../atoms';
import { JobProgressIndicator, JobStatusActionButton } from '../../molecules';
import { JobListMain } from '../JobListMain';
import { ResponsiveFilters } from '../ResponsiveFilters';
import type { ColumnConfig } from '../JobListMain';

/* ============================================
 * CONSTANTS
 * ============================================ */

/** Client-side page size for the embedded list. */
const PER_PAGE = 8;

/** Completion filter chips shown in the simplified filter row. */
type CompletionFilter = 'all' | 'open' | 'completed';

const COMPLETION_FILTERS: { key: CompletionFilter; label: string }[] = [
	{ key: 'all', label: 'All' },
	{ key: 'open', label: 'Not Completed' },
	{ key: 'completed', label: 'Completed' },
];

/* ============================================
 * DATE HELPERS
 * ============================================
 * The feed's `start` is either a bare date ("2026-08-17", all-day) or an
 * ISO datetime. A bare date must NOT go through `new Date(...)` for
 * display — JS parses it as UTC midnight, which renders as the previous
 * evening in western timezones. These helpers therefore work off the
 * date substring and only touch Date for genuine datetimes.
 */

const DATE_KEY_RE = /^(\d{4}-\d{2}-\d{2})/;

/** The event's calendar date as a local `yyyy-mm-dd` key. */
function eventDateKey(event: ScheduledEvent): string {
	const source = event.scheduledDate || event.start || '';
	const match = DATE_KEY_RE.exec(source);
	if (match) return match[1];
	const d = new Date(source);
	if (Number.isNaN(d.getTime())) return '';
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Start-time label ("3:30pm EST") for timed events; null for all-day. */
function eventTimeLabel(event: ScheduledEvent): string | null {
	if (event.allDay || !event.start || !event.start.includes('T')) return null;
	const d = new Date(event.start);
	if (Number.isNaN(d.getTime())) return null;
	const time = d
		.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' })
		.replace(' ', '')
		.toLowerCase();
	return `${time} EST`;
}

/**
 * Compact date cell for the two-day window — leads with TODAY/YESTERDAY
 * so the row is scannable without reading the numeric date. Mirrors the
 * DateTimeStack layout used by the full list pages.
 */
const TaskDateCell: React.FC<{ event: ScheduledEvent }> = ({ event }) => {
	const key = eventDateKey(event);
	const isToday = key === isoDaysAgo(0);
	const isYesterday = key === isoDaysAgo(1);

	/* Relative label; falls back to the weekday for any out-of-window
	 * date (defensive — the fetch window is exactly these two days). */
	let dayLabel = 'YESTERDAY';
	if (isToday) {
		dayLabel = 'TODAY';
	} else if (!isYesterday && key) {
		const [y, m, d] = key.split('-').map(Number);
		dayLabel = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
	}

	/* MM/DD/YY from the date key — no Date round-trip. */
	const dateLabel = key ? `${key.slice(5, 7)}/${key.slice(8, 10)}/${key.slice(2, 4)}` : '—';
	const timeLabel = eventTimeLabel(event);

	return (
		<div className="dashboard-task-list__date flex flex-col leading-tight whitespace-nowrap">
			<span className={`text-10px font-semibold uppercase tracking-wide ${isToday ? 'text-brand-primary' : 'text-brand-text-subtle'}`}>{dayLabel}</span>
			<span className="text-14px font-medium text-brand-text">{dateLabel}</span>
			{timeLabel && <span className="text-10px text-brand-text-muted">{timeLabel}</span>}
		</div>
	);
};

/* ============================================
 * MAIN COMPONENT
 * ============================================ */

export interface DashboardTaskListProps {
	/** Optional extra classes merged onto the root element. */
	className?: string;
}

export const DashboardTaskList: React.FC<DashboardTaskListProps> = ({ className }) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { hasAnyRole } = useAuth();

	/* The rows are individual work orders, so "view all" goes to the WO
	 * list for every role. The Project Work Orders page (admin-gated in
	 * the nav) is reachable through the per-row project association
	 * below instead. */
	const canOpenProjectWorkOrders = hasAnyRole(['super_admin', 'admin']);

	/* ---- Simplified filter state (section-local UI state) ---- */
	const [completionFilter, setCompletionFilter] = useState<CompletionFilter>('all');
	const [filterUserId, setFilterUserId] = useState('');
	const [filterCompanyId, setFilterCompanyId] = useState('');
	const [currentPage, setCurrentPage] = useState(1);

	/* ---- Events read (TanStack Query) ----
	 * Two-day window: yesterday through today, matched against
	 * scheduled_date (WOs) / inspection_date (Inspections) server-side.
	 * The day strings are part of the query key, so the section rolls
	 * over to a fresh window automatically after midnight. */
	const from = isoDaysAgo(1);
	const to = isoDaysAgo(0);

	const query = useQuery({
		queryKey: qk.scheduledEvents.list({ from, to, scope: 'dashboardTasks' }),
		queryFn: async (): Promise<ScheduledEvent[]> => {
			const res = await scheduledEventsService.list({ from, to });
			/* Server wraps the payload in { data: [...] }; the service camelizes. */
			return ((res.data as any)?.data ?? []) as ScheduledEvent[];
		},
	});

	const events: ScheduledEvent[] = useMemo(() => query.data ?? [], [query.data]);
	const isLoading = query.isLoading;
	const error = query.error ? (query.error as Error).message : null;

	/* ---- Start CTA (pending rows) ----
	 * Mirrors the list pages: PATCH the record into 'in_progress', then
	 * invalidate so the row's progress bar + CTA update. Navigation to
	 * the workflow surface happens via the button's onActive callback. */
	const startJobMutation = useMutation({
		/* Explicit Promise<unknown> — the two services resolve different
		 * record shapes and the union confuses useMutation's inference. */
		mutationFn: (event: ScheduledEvent): Promise<unknown> =>
			event.recordType === 'work_order'
				? workOrderService.update(String(event.recordId), { status: 'in_progress' })
				: inspectionService.update(String(event.recordId), { status: 'in_progress' }),
		onSuccess: (_res, event) => {
			queryClient.invalidateQueries({ queryKey: qk.scheduledEvents.all });
			queryClient.invalidateQueries({
				queryKey: event.recordType === 'work_order' ? qk.workOrders.all : qk.inspections.all,
			});
		},
		onError: (err) => {
			console.error('[DashboardTaskList] Failed to start job:', err);
		},
	});

	/* ---- Filter options, derived from the fetched window ----
	 * Deriving from the events themselves keeps the options in the
	 * user's scope for free (no extra user/company endpoints, which
	 * technicians can't call anyway) and never offers a choice that
	 * would match zero rows. */
	const userOptions = useMemo(() => {
		const map = new Map<string, string>();
		for (const event of events) {
			for (const person of event.assignees) {
				if (person.id && person.name) map.set(String(person.id), person.name);
			}
		}
		return Array.from(map.entries())
			.map(([value, label]) => ({ value, label }))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [events]);

	const companyOptions = useMemo(() => {
		const map = new Map<string, string>();
		for (const event of events) {
			if (event.companyId && event.companyName) map.set(String(event.companyId), event.companyName);
		}
		return Array.from(map.entries())
			.map(([value, label]) => ({ value, label }))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [events]);

	/* ---- Client-side filtering ----
	 * "Completed" reuses getJobProgress's canonical done-set (completed /
	 * invoiced / closed for WOs, completed for inspections); "Not
	 * Completed" is everything still in flight — cancelled rows only
	 * appear under All. */
	const filteredEvents = useMemo(() => {
		return events.filter((event) => {
			if (completionFilter !== 'all') {
				const progress = getJobProgress(event.status, event.recordType);
				if (completionFilter === 'completed' && !progress.isComplete) return false;
				if (completionFilter === 'open' && (progress.isComplete || progress.isCancelled)) return false;
			}
			if (filterUserId && !event.assignees.some((person) => String(person.id) === filterUserId)) return false;
			if (filterCompanyId && String(event.companyId) !== filterCompanyId) return false;
			return true;
		});
	}, [events, completionFilter, filterUserId, filterCompanyId]);

	/* ---- Client-side pagination over the filtered window ---- */
	const totalItems = filteredEvents.length;
	const totalPages = Math.max(1, Math.ceil(totalItems / PER_PAGE));
	/* Clamp so a shrinking result set can't strand the pager past the end. */
	const safePage = Math.min(currentPage, totalPages);
	const pageItems = useMemo(
		() => filteredEvents.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE),
		[filteredEvents, safePage]
	);

	/** Filter setters reset pagination so a narrowed list starts at page 1. */
	const applyCompletionFilter = (value: CompletionFilter) => { setCompletionFilter(value); setCurrentPage(1); };
	const applyUserFilter = (value: string) => { setFilterUserId(value); setCurrentPage(1); };
	const applyCompanyFilter = (value: string) => { setFilterCompanyId(value); setCurrentPage(1); };

	/* Counted, not just flagged: the collapsed Filters button shows the number. */
	const activeFilterCount = [completionFilter !== 'all', filterUserId !== '', filterCompanyId !== ''].filter(Boolean).length;
	const hasActiveFilters = activeFilterCount > 0;
	const resetFilters = () => { setCompletionFilter('all'); setFilterUserId(''); setFilterCompanyId(''); setCurrentPage(1); };

	/* ============================================
	 * TABLE COLUMN CONFIGURATION
	 * ============================================ */

	const columns: ColumnConfig[] = [
		{
			key: 'date',
			header: 'Date',
			render: (event: ScheduledEvent) => <TaskDateCell event={event} />,
		},
		{
			key: 'type',
			header: 'Type',
			render: (event: ScheduledEvent) => <EventTypeBadge type={getEventCategory(event)} size="sm" />,
		},
		{
			key: 'job',
			header: 'Job',
			render: (event: ScheduledEvent) => (
				<div className="min-w-0">
					<button
						type="button"
						onClick={() => navigate(event.detailUrl)}
						className="text-left text-14px font-semibold text-brand-accent hover:underline line-clamp-1"
					>
						{event.title}
					</button>
					{/* Inspection titles already lead with the location, so the
					    sub-line is only useful on work-order rows. */}
					{event.recordType === 'work_order' && event.locationName && (
						<div className="text-12px text-brand-text-muted line-clamp-1 mt-2px">{event.locationName}</div>
					)}
					{/* Parent Project Work Order association — rendered as a
					    link for roles that can open the project page, plain
					    text for everyone else. */}
					{event.projectWorkOrderName && (
						<div className="text-11px text-brand-text-muted line-clamp-1 mt-2px">
							{'Project: '}
							{canOpenProjectWorkOrders && event.projectWorkOrderId ? (
								<button
									type="button"
									onClick={() => navigate(`/project-work-orders/${event.projectWorkOrderId}`)}
									className="font-semibold text-brand-text-muted hover:text-brand-primary hover:underline"
								>
									{event.projectWorkOrderName}
								</button>
							) : (
								<span className="font-semibold">{event.projectWorkOrderName}</span>
							)}
						</div>
					)}
				</div>
			),
		},
		{
			key: 'company',
			header: 'Company',
			render: (event: ScheduledEvent) => (
				<span className="text-12px text-brand-text">{event.companyName || '—'}</span>
			),
		},
		{
			key: 'assignees',
			header: 'Assigned To',
			render: (event: ScheduledEvent) => (
				event.assignees.length > 0 ? (
					<div className="flex flex-col gap-2px">
						{event.assignees.slice(0, 2).map((person) => (
							<span key={person.id} className="text-12px text-brand-text line-clamp-1">{person.name}</span>
						))}
						{event.assignees.length > 2 && (
							<span className="text-11px text-brand-text-muted">+{event.assignees.length - 2} more</span>
						)}
					</div>
				) : (
					<span className="text-12px text-brand-text-muted">Unassigned</span>
				)
			),
		},
		{
			key: 'status',
			header: 'Status',
			render: (event: ScheduledEvent) => (
				<JobProgressIndicator status={event.status} type={event.recordType} />
			),
		},
		{
			/* Same status-driven CTA the list pages use — Start for pending
			   rows (PATCHes to in_progress, then deep-links into the
			   ?workflow=1 overlay), Resume for in-flight rows, View for
			   terminal ones. */
			key: 'primaryAction',
			header: '',
			render: (event: ScheduledEvent) => (
				<JobStatusActionButton
					status={event.status}
					ariaLabel={`${event.status} action for ${event.title}`}
					onStart={() => startJobMutation.mutate(event)}
					onActive={() => navigate(`${event.detailUrl}?workflow=1`)}
					onView={() => navigate(event.detailUrl)}
				/>
			),
		},
	];

	/* ============================================
	 * RENDER
	 * ============================================ */

	return (
		<div className={['dashboard-task-list', className].filter(Boolean).join(' ')} data-ref="organism-dashboard-task-list">
			{/* Section header — title + the two "view all" jump links */}
			<div className="dashboard-task-list__header flex flex-wrap items-end justify-between gap-12px mb-16px">
				<div>
					<h2 className="text-18px font-heading uppercase tracking-wide text-brand-heading">MY TASKS</h2>
					<p className="text-13px text-brand-text-muted mt-4px">
						Your work orders and inspections from today and yesterday.
					</p>
				</div>
				<div className="flex items-center gap-16px flex-wrap">
					<button
						type="button"
						onClick={() => navigate('/work-orders')}
						className="dashboard-task-list__view-all inline-flex items-center min-h-44px desktop:min-h-32px text-12px font-semibold uppercase tracking-wide text-brand-primary hover:text-brand-primary-hover transition-colors"
					>
						VIEW ALL WORK ORDERS →
					</button>
					<button
						type="button"
						onClick={() => navigate('/inspections')}
						className="dashboard-task-list__view-all inline-flex items-center min-h-44px desktop:min-h-32px text-12px font-semibold uppercase tracking-wide text-brand-primary hover:text-brand-primary-hover transition-colors"
					>
						VIEW ALL INSPECTIONS →
					</button>
				</div>
			</div>

			{/* Simplified filter row — completion chips + user/company selects.
			    Below desktop it folds into a Filters button + pop-up so the task
			    table keeps the width (ResponsiveFilters). The chips stay grouped
			    in one element so the pop-up keeps them on one row. */}
			<ResponsiveFilters
				title="Filter tasks"
				activeCount={activeFilterCount}
				onReset={resetFilters}
				className="dashboard-task-list__filters flex flex-wrap items-center gap-8px mb-16px"
				triggerClassName="dashboard-task-list__filters mb-16px"
				dataRef="organism-dashboard-task-list-filters"
			>
				<div className="dashboard-task-list__completion flex flex-wrap items-center gap-8px" role="group" aria-label="Show tasks">
				{COMPLETION_FILTERS.map((filter) => {
					const isActive = completionFilter === filter.key;
					return (
						<button
							key={filter.key}
							type="button"
							onClick={() => applyCompletionFilter(filter.key)}
							className={[
								'px-16px py-6px rounded-full text-12px font-semibold uppercase tracking-wide transition-colors min-h-44px desktop:min-h-36px',
								isActive
									? 'bg-brand-primary text-white'
									: 'bg-gray-100 text-brand-text-muted hover:bg-gray-200',
							].join(' ')}
						>
							{filter.label}
						</button>
					);
				})}
				</div>

				{/* User select — hidden when the scope only contains one person
				    (e.g. a technician's own feed). 16px below desktop, like every
				    field: at a bare 13px an iPhone zoomed in on each tap. */}
				{userOptions.length > 1 && (
					<select
						value={filterUserId}
						onChange={(e) => applyUserFilter(e.target.value)}
						aria-label="Filter tasks by user"
						className="px-12px py-8px min-h-44px desktop:min-h-36px text-16px desktop:text-13px border border-brand-border rounded-6px bg-white text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
					>
						<option value="">All users</option>
						{userOptions.map((opt) => (
							<option key={opt.value} value={opt.value}>{opt.label}</option>
						))}
					</select>
				)}

				{/* Company select — hidden for single-company scopes. */}
				{companyOptions.length > 1 && (
					<select
						value={filterCompanyId}
						onChange={(e) => applyCompanyFilter(e.target.value)}
						aria-label="Filter tasks by company"
						className="px-12px py-8px min-h-44px desktop:min-h-36px text-16px desktop:text-13px border border-brand-border rounded-6px bg-white text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
					>
						<option value="">All companies</option>
						{companyOptions.map((opt) => (
							<option key={opt.value} value={opt.value}>{opt.label}</option>
						))}
					</select>
				)}
			</ResponsiveFilters>

			{/* Shared list body — same organism the WO / Inspection list pages
			    render inside JobListTemplate. */}
			<JobListMain
				columns={columns}
				items={pageItems}
				isLoading={isLoading}
				error={error}
				currentPage={safePage}
				totalPages={totalPages}
				totalItems={totalItems}
				perPage={PER_PAGE}
				onPageChange={setCurrentPage}
				onRetry={() => query.refetch()}
				emptyMessage={hasActiveFilters
					? 'No tasks match the selected filters.'
					: 'No work orders or inspections scheduled for today or yesterday.'}
				skeletonRows={4}
			/>
		</div>
	);
};

export default DashboardTaskList;
